import React, { useMemo } from "react";
import { motion } from "framer-motion";

const TROFEU_EMOJI = ["🥇", "🥈", "🥉", "🏅", "🎖️"];

const norm = (s = "") =>
  String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// ------ Helpers ------
function parseValorBR(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v).trim();
  if (/^[\d\.]+,\d{2}$/.test(s)) {
    return Number(s.replace(/\./g, "").replace(",", "."));
  }
  const n = Number(s.replace(/[^\d\.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

// ------ Componente ------
export default function RankingCaptadores({
  usuarios = [],
  recebimentos = [],
  leads = [],
  empresaId,
  lojas = [],
  strictVendedor = false,
  vazioPlaceholder = true,
  debug = false,
}) {
  // Sempre força mês/ano atual
  const hoje = new Date();
  const mesSel = hoje.getMonth() + 1;
  const anoSel = hoje.getFullYear();
  const alvoPrefix = `${anoSel}-${String(mesSel).padStart(2, "0")}`;

  // empresas alvo (sempre como string)
  const empresasParaRanking = useMemo(() => {
    if (lojas?.length > 0) return lojas.map((l) => String(l.id));
    if (empresaId != null) return [String(empresaId)];

    const ids = new Set();
    for (const r of recebimentos || [])
      if (r?.empresa_id != null) ids.add(String(r.empresa_id));
    if (!ids.size) {
      for (const l of leads || [])
        if (l?.empresa_id != null) ids.add(String(l.empresa_id));
    }
    return Array.from(ids);
  }, [lojas, empresaId, recebimentos, leads]);

  // --- RANKING do mês/ano vigente
  const ranking = useMemo(() => {
    if (
      !Array.isArray(recebimentos) ||
      !Array.isArray(leads) ||
      !empresasParaRanking.length
    ) {
      return [];
    }

    // indexa leads por (empresa_id + lead_id)
    const idxLead = new Map();
    for (const l of leads) {
      const key = `${String(l?.empresa_id)}::${String(l?.id)}`;
      idxLead.set(key, l);
    }

    const acumulado = {};

    for (const receb of recebimentos) {
      if (!empresasParaRanking.includes(String(receb?.empresa_id))) continue;

      // --- FILTRO: só mês/ano vigente (string prefix) ---
      const dataStr = String(receb?.data_recebimento || "").trim();
      if (!dataStr.startsWith(alvoPrefix)) continue;

      // encontra a lead correspondente
      const lead = idxLead.get(
        `${String(receb?.empresa_id)}::${String(receb?.lead_id)}`
      );
      if (!lead) continue;

      // status deve ser fechado / fechado parcial
      const st = norm(lead?.status_negociacao || "");
      if (st !== "fechado" && st !== "fechado parcial") continue;

      const captadorId = lead?.usuario_criador_id;
      if (!captadorId) continue;

      const val =
        typeof receb?.valor === "number"
          ? Number(receb.valor) || 0
          : parseValorBR(receb?.valor);
      if (!val) continue;

      acumulado[captadorId] = (acumulado[captadorId] || 0) + val;
    }

    return Object.entries(acumulado)
      .map(([captadorId, total]) => {
        const u = usuarios.find((x) => String(x?.id) === String(captadorId));
        if (strictVendedor && u && norm(u?.cargo) !== "vendedor") return null;
        return {
          id: String(captadorId),
          nome: u?.nome || `Usuário #${captadorId}`,
          total: Number(total || 0),
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [
    usuarios,
    recebimentos,
    leads,
    empresasParaRanking,
    alvoPrefix,
    strictVendedor,
  ]);

  // ------ Render ------
  if (!ranking.length) {
    return vazioPlaceholder ? (
      <div className="mt-1 text-[11px] opacity-60">
        <div className="inline-flex items-center gap-2 px-2 py-1 rounded border border-dashed">
          <span>🏁 Ranking de captadores</span>
          <span>•</span>
          <span>
            sem dados no mês atual ({String(mesSel).padStart(2, "0")}/{anoSel})
          </span>
          {debug && (
            <span className="ml-2">
              (dbg: usuarios {usuarios?.length || 0} • receb{" "}
              {recebimentos?.length || 0} • leads {leads?.length || 0} • emp{" "}
              {empresasParaRanking?.length || 0})
            </span>
          )}
        </div>
      </div>
    ) : (
      <div style={{ display: "none" }} />
    );
  }

  return (
    <div className="mt-1 text-[11px] overflow-x-auto">
      <div className="flex flex-row items-stretch gap-1 whitespace-nowrap">
        {ranking.map((item, index) => (
          <div
            key={item.id}
            className="inline-flex min-w-[110px] px-2 py-1 rounded overflow-hidden select-none"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 220 }}
              className="flex flex-col items-center justify-center text-center w-full will-change-transform transform-gpu"
            >
              <div className="text-base leading-none">
                {TROFEU_EMOJI[index] || "🏅"}
              </div>
              <div className="text-gray-800 font-medium truncate max-w-[120px]">
                {(item.nome || "").split(" ").slice(0, 2).join(" ")}
              </div>
              <div className="text-blue-700 font-bold">
                R{"$ "}
                {Number(item.total).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
}
